import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const MIN_QUERY_LENGTH = 3;
const RESULT_LIMIT = 25;

export function PharmacyProductPicker({ onAdd }) {
  const [connectionId, setConnectionId] = useState(null);
  const [connectionReady, setConnectionReady] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const searchSequence = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/pharmacy/current-connection").then((response) => {
      if (!cancelled && response.data?.connection_id) setConnectionId(response.data.connection_id);
    }).catch(() => {
      if (!cancelled) setConnectionId(null);
    }).finally(() => {
      if (!cancelled) setConnectionReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const term = query.trim();
    const sequence = ++searchSequence.current;
    if (!connectionId || term.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearchId("");
      setLoading(false);
      setOpen(false);
      return undefined;
    }
    setResults([]);
    setSearchId("");
    setLoading(true);
    setOpen(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get("/pharmacy/products", { params: { connection_id: connectionId, q: term, limit: RESULT_LIMIT } });
        if (searchSequence.current !== sequence) return;
        setResults(response.data?.products || []);
        setSearchId(response.data?.search_id || "");
      } catch (error) {
        if (searchSequence.current !== sequence) return;
        setResults([]);
        setSearchId("");
        toast.error(error?.response?.data?.detail || "Pharmacy products could not be loaded");
      } finally {
        if (searchSequence.current === sequence) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [connectionId, query]);

  const select = async (candidate) => {
    if (!connectionId || !searchId) return;
    try {
      const response = await api.post("/pharmacy/product-selections", { connection_id: connectionId, search_id: searchId, candidate_id: candidate.candidate_id });
      onAdd({
        draft_id: crypto.randomUUID(),
        medicine_name: response.data.display_label,
        pharmacy_product_selection_id: response.data.selection_id,
        pharmacy_quantity: "1",
        pharmacy_unit_name: response.data.unit_name,
        pharmacy_available_quantity: response.data.available_quantity,
        pharmacy_lot_number: response.data.lot_number,
        pharmacy_expiry_date: response.data.expiry_date,
        pharmacy_selection_expires_at: response.data.expires_at,
      });
      setResults([]);
      setQuery("");
      setOpen(false);
      toast.success("Medicine added to this prescription");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "This pharmacy product is no longer available");
    }
  };

  if (!connectionReady) return <p className="mt-3 text-xs text-slate-500">Checking pharmacy availability…</p>;
  if (!connectionId) return <p className="mt-3 text-xs text-slate-500">Pharmacy product selection is unavailable until one clinic pharmacy connection is enabled.</p>;

  return <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3" data-testid="pharmacy-product-picker">
    <p className="text-xs text-slate-600 mb-2">Search and add each medicine needed for this prescription. You can add several medicines before saving. Pricing and supplier identifiers are not shown.</p>
    <div className="relative">
      <input aria-label="Search pharmacy medicines" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls="pharmacy-product-options" className="mg-input py-2 text-sm w-full" value={query} onChange={(event) => setQuery(event.target.value)} onFocus={() => query.trim().length >= MIN_QUERY_LENGTH && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="Type 3 or more letters" autoComplete="off" />
      {open && <div id="pharmacy-product-options" role="listbox" className="absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-blue-100 bg-white shadow-xl">
        {loading && <p className="px-3 py-3 text-xs text-slate-500">Searching pharmacy catalogue…</p>}
        {!loading && results.length === 0 && <p className="px-3 py-3 text-xs text-slate-500">No matching medicine found.</p>}
        {!loading && results.map((product) => {
          const available = Number(product.available_quantity);
          const quantity = Number.isFinite(available) ? available.toLocaleString() : product.available_quantity;
          const expiry = product.expiry_date ? new Date(`${product.expiry_date}T00:00:00`).toLocaleDateString() : null;
          return <button key={product.candidate_id} type="button" role="option" className="block w-full border-b border-blue-50 px-3 py-2.5 text-left last:border-0 hover:bg-blue-50" onMouseDown={(event) => event.preventDefault()} onClick={() => select(product)}><span className="block text-sm font-semibold text-mg-navy">{product.display_label}</span><span className="mt-0.5 block text-xs text-slate-500">{product.lot_number ? `Lot ${product.lot_number} · Expires ${expiry} · ` : ""}{product.available_quantity != null ? `${quantity} ${product.unit_name || "base units"} available` : "Availability not shown"}</span></button>;
        })}
      </div>}
    </div>
  </div>;
}

export default PharmacyProductPicker;
